import { useState, type FormEvent } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Toast from "@/components/feature/Toast";
import { usePageMeta } from "@/hooks/usePageMeta";

const channels = [
  { icon: "ri-mail-line", label: "合作与投稿邮箱", value: "hello@yingshigongchang.com" },
  { icon: "ri-customer-service-2-line", label: "用户反馈邮箱", value: "support@yingshigongchang.com" },
  { icon: "ri-weibo-fill", label: "官方微博", value: "@影视工厂" },
  { icon: "ri-tv-2-line", label: "哔哩哔哩", value: "影视工厂官方账号" },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting">("idle");
  const [toast, setToast] = useState("");
  const [formError, setFormError] = useState("");

  usePageMeta(
    "联系我们 - 影视工厂",
    "欢迎通过影视工厂联系我们表单进行合作咨询、内容投稿与反馈建议，我们会在收到后尽快回复。",
    "联系影视工厂,影视工厂合作,影视投稿,反馈建议"
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setToast("感谢你的留言，我们已收到");
      window.setTimeout(() => setToast(""), 2200);
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value as string);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch("https://readdy.ai/api/form/db7rfvatp9sllopf7j9g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed:
        | { code?: string; message?: string; meta?: { message?: string; detail?: string } }
        | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const message =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof message === "string" && message.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setToast("提交成功，感谢你的留言！");
        window.setTimeout(() => setToast(""), 2400);
        form.reset();
      } else {
        setFormError(
          typeof message === "string" && message ? message : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setFormError("网络异常，提交失败，请稍后重试。");
    } finally {
      setStatus("idle");
    }
  };

  return (
    <div className="bg-background-50">
      <div className="bg-background-100 border-b border-background-200">
        <div className="mx-auto max-w-page px-4 md:px-6 py-8 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
          <h1 className="mt-5 font-heading text-3xl md:text-5xl text-foreground-950 tracking-wide">
            联系我们
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-3xl leading-8">
            无论是内容合作、投稿建议还是使用反馈，都欢迎通过下面的表单或邮箱与我们取得联系。
            我们的团队会在收到消息后尽快回复你。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <h2 className="font-heading text-2xl text-foreground-950 tracking-wide">
              在线留言
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              请填写以下信息，带 <span className="text-accent-600">*</span> 的为必填项。
            </p>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 space-y-5"
            >
              <div>
                <label htmlFor="contact-name" className="block text-sm text-foreground-800 mb-2">
                  姓名 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的姓名"
                  className="w-full h-12 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm text-foreground-800 mb-2">
                  邮箱 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="请输入你的邮箱地址"
                  className="w-full h-12 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm text-foreground-800 mb-2">
                  留言内容 <span className="text-accent-600">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请简单描述你的需求或建议（最多 500 字）"
                  className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                />
              </div>

              <input
                type="text"
                name="website_alt"
                className="field-aux-note"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {formError && (
                <p className="text-sm text-accent-700 bg-accent-50 rounded-md px-4 py-3">
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full sm:w-auto px-8 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {status === "submitting" ? "提交中…" : "提交留言"}
              </button>
            </form>

            <p className="mt-5 text-xs text-foreground-500">
              本站为演示站点，表单提交后我们会通过邮箱或官方账号回复。请勿提交敏感个人信息。
            </p>
          </div>

          <div className="space-y-6">
            <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
              <h2 className="font-heading text-xl text-foreground-950 tracking-wide">
                联系方式
              </h2>
              <ul className="mt-5 space-y-5">
                {channels.map((channel) => (
                  <li key={channel.label} className="flex items-start gap-4">
                    <span className="w-10 h-10 shrink-0 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center">
                      <i className={`${channel.icon} text-lg`} />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">{channel.label}</p>
                      <p className="mt-1 text-sm text-foreground-900 break-all">{channel.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-6 md:p-8">
              <h2 className="font-heading text-xl text-secondary-900 tracking-wide flex items-center gap-2">
                <i className="ri-information-line" />
                反馈说明
              </h2>
              <ul className="mt-4 space-y-3 text-sm text-secondary-900 leading-7">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-1 shrink-0" />
                  内容相关问题我们会在 1-2 个工作日内处理。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-1 shrink-0" />
                  合作洽谈请尽量注明合作类型与预期形式。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-1 shrink-0" />
                  如需投稿，欢迎附上作品链接与简要说明。
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Toast message={toast} visible={Boolean(toast)} />
    </div>
  );
}